import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import AuthService from '../services/auth.service';
import {
  getTimeUntilExpiration,
  needsRefresh,
  setTokenTimestamp,
  clearTokenTimestamp,
} from '../utils/tokenUtils';
import { permissionGranted, anyPermissionGranted, collectPermissionNames, normalizeRoleNames, hasAttendanceAccess } from '../utils/permissionAliases';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const refreshTimeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Token refresh interval (20 hours - refresh before 24h expiration)
  const REFRESH_INTERVAL = 20 * 60 * 60 * 1000; // 20 hours in milliseconds

  const clearRefreshTimeout = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  const scheduleTokenRefresh = () => {
    clearRefreshTimeout();
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Calculate refresh time based on token expiration
    const timeUntilExpiration = getTimeUntilExpiration(token);
    const refreshTime = Math.max(timeUntilExpiration - (4 * 60 * 60 * 1000), 5 * 60 * 1000); // 4 hours before expiration, minimum 5 minutes
    
    refreshTimeoutRef.current = setTimeout(async () => {
      await refreshToken();
    }, refreshTime);
    
    console.log(`Token refresh scheduled in ${Math.round(refreshTime / (60 * 1000))} minutes`);
  };

  const refreshToken = async (manual = false) => {
    if (isRefreshingRef.current) return;
    
    try {
      isRefreshingRef.current = true;
      const response = await AuthService.refresh();
      const { user, token, permissions, roles } = response.data;
      
      localStorage.setItem('token', token);
      setTokenTimestamp();
      setUser(user);
      setPermissions(collectPermissionNames(user, permissions));
      setRoles(normalizeRoleNames(roles));
      setIsAuthenticated(true);
      
      // Schedule next refresh
      scheduleTokenRefresh();
      
      console.log(`Token refreshed successfully ${manual ? '(manual)' : '(automatic)'}`);
      return { success: true };
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      await logout();
      return { success: false, error: error.message };
    } finally {
      isRefreshingRef.current = false;
    }
  };

  // Manual refresh function for components to call
  const manualRefresh = async () => {
    return await refreshToken(true);
  };

  const logout = async () => {
    try {
      await AuthService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearRefreshTimeout();
      localStorage.removeItem('token');
      clearTokenTimestamp();
      setUser(null);
      setPermissions([]);
      setRoles([]);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const onPublicAuthPage =
        window.location.pathname.startsWith('/reset-password') ||
        window.location.pathname.startsWith('/login');

      if (token && onPublicAuthPage) {
        localStorage.removeItem('token');
        clearTokenTimestamp();
        setLoading(false);
        return;
      }

      if (token) {
        // First, verify token is valid by calling /me
        try {
          const response = await AuthService.me();
          setUser(response.data.user);
          setPermissions(collectPermissionNames(response.data.user, response.data.permissions));
          setRoles(normalizeRoleNames(response.data.roles));
          setIsAuthenticated(true);
          
          // Check if token needs refresh AFTER successful auth check
          // Don't refresh immediately - let it happen naturally or schedule it
          if (needsRefresh(token)) {
            // Schedule refresh soon (5 minutes) instead of immediately
            console.log('Token needs refresh, scheduling refresh in 5 minutes...');
            refreshTimeoutRef.current = setTimeout(async () => {
              await refreshToken();
            }, 5 * 60 * 1000); // 5 minutes
          } else {
            // Schedule token refresh normally
            scheduleTokenRefresh();
          }
        } catch (error) {
          console.error('Auth initialization failed:', error);
          // If /me fails, token is invalid - clear it and logout
          await logout();
        }
      }
      setLoading(false);
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      clearRefreshTimeout();
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await AuthService.login(email, password);
      const { user, token, permissions, roles } = response.data;
      
      localStorage.setItem('token', token);
      setTokenTimestamp();
      setUser(user);
      setPermissions(collectPermissionNames(user, permissions));
      setRoles(normalizeRoleNames(roles));
      setIsAuthenticated(true);
      
      // Schedule token refresh after successful login
      scheduleTokenRefresh();
      
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      const validationError = data?.errors?.email?.[0];
      return {
        success: false,
        error: validationError || data?.message || error.message || 'Login failed',
      };
    }
  };

  const hasRole = (role) => normalizeRoleNames(roles).includes(role);

  const hasPermission = (permission) => {
    if (normalizeRoleNames(roles).includes('admin')) {
      return true;
    }
    return permissionGranted(permissions, permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (normalizeRoleNames(roles).includes('admin')) {
      return true;
    }
    return anyPermissionGranted(permissions, permissionList);
  };

  const hasAnyRole = (roleList) => {
    return roleList.some((role) => normalizeRoleNames(roles).includes(role));
  };

  // Re-fetch /me (e.g. after tenant settings change) without a full reload.
  const refreshUser = async () => {
    try {
      const response = await AuthService.me();
      setUser(response.data.user);
      setPermissions(collectPermissionNames(response.data.user, response.data.permissions));
      setRoles(normalizeRoleNames(response.data.roles));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    permissions,
    roles,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshToken: manualRefresh,
    refreshUser,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAnyRole,
    hasAttendanceAccess: () => hasAttendanceAccess(permissions),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
